import React from "react";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[80] h-full w-full max-w-md bg-[#0a0a0a] border-l border-[#27272a] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#27272a]">
              <h2 className="font-display text-2xl">
                Your Cart <span className="text-[#00f0ff] text-lg">({count})</span>
              </h2>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white">
                <X size={22} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
                <p className="text-zinc-400">Your garage is empty.</p>
                <Link
                  to="/category/rims"
                  onClick={() => setOpen(false)}
                  className="bttf-gradient text-black font-semibold uppercase tracking-wide px-6 py-3 text-sm"
                >
                  Start Building
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {items.map((i) => (
                    <div key={i.product_id} className="flex gap-4 border-b border-[#1c1c1c] pb-4" data-testid={`cart-line-${i.slug}`}>
                      <img src={i.image} alt={i.name} className="h-20 w-20 object-cover bg-[#111] border border-[#27272a]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg leading-tight truncate">{i.name}</div>
                        <div className="text-[#00f0ff] text-sm">{money(i.price)}</div>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center border border-[#27272a]">
                            <button className="p-1.5 text-zinc-400 hover:text-white" onClick={() => updateQty(i.product_id, i.quantity - 1)} data-testid={`cart-dec-${i.slug}`}>
                              <Minus size={14} />
                            </button>
                            <span className="px-3 text-sm">{i.quantity}</span>
                            <button className="p-1.5 text-zinc-400 hover:text-white" onClick={() => updateQty(i.product_id, i.quantity + 1)} data-testid={`cart-inc-${i.slug}`}>
                              <Plus size={14} />
                            </button>
                          </div>
                          <button className="text-zinc-500 hover:text-red-400" onClick={() => removeItem(i.product_id)} data-testid={`cart-remove-${i.slug}`}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="font-bold text-sm">{money(i.price * i.quantity)}</div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#27272a] px-6 py-5 space-y-4">
                  <div className="flex justify-between text-lg">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="font-bold" data-testid="cart-subtotal">{money(subtotal)}</span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={() => setOpen(false)}
                    data-testid="checkout-link"
                    className="block text-center bttf-gradient text-black font-bold uppercase tracking-wider py-4 hover:opacity-90 transition-opacity"
                  >
                    Checkout
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
